import { useMemo } from 'react';
import {
  getCurrentChannel,
  getUserId,
  useAppSelector,
  ChannelTypeEnum,
  store,
  getWorkspaceId,
  getSelectedPrincipalAccount,
  IssueStatus,
  getGrowQueryConversationThread,
} from '@zonofi/seller-rtk-store';
import { titleTruncate } from '@zonofi/common';
import { isEmpty, isNull } from 'lodash';
import { i18nService } from '@zonofi/i18n';

export const useGetCurrentChannelData = () => {
  const channel = useAppSelector(getCurrentChannel);
  const selectedPrincipal = useAppSelector(getSelectedPrincipalAccount);
  const growQueryConversationThread = useAppSelector(
    getGrowQueryConversationThread
  );

  const uuid = getUserId(store.getState());
  const workspaceId = getWorkspaceId(store.getState());
  const {
    id: channelId = '',
    channelName,
    companyTeamMembers = [],
    customers = [],
    teamMembers = [],
    customerId,
    type,
    ownerId,
    isActive,
    emojiId,
    sellerWorkspaceId,
    isLockedCustomer,
    issueStatus,
    transactionType,
    optedOut,
    marketingMessageLimitExhausted,
    customerInfo,
    customerName,
    customerCode,
    channelType,
    channelSubType,
    unreadCount = 0,
  } = channel || {};
  const customerTeamMembers = customers
    .flatMap((x) => x.team)
    ?.filter((x) => x?.inviteType !== 'teamowner');
  const customer = customers.find((x) => x.inviteId === customerId);
  const customerNames = customers.map((x) => x.name);
  const customerTeamMemberNames = customerTeamMembers.map(
    (x) => x?.employeeName
  );
  const teamMemberNames = teamMembers.map((x) => x.employeeName);
  const companyTeamMemberNames = companyTeamMembers.map((x) => x.employeeName);
  const channelMembers = [
    ...customerNames,
    ...teamMemberNames,
    ...customerTeamMemberNames,
    ...companyTeamMemberNames,
  ];
  const channelMembersNames = channelMembers
    .filter((x) => !isNull(x))
    .join(', ');

  const channelMembersCount = `${
    customer
      ? `${titleTruncate(customer?.distributorCode as string, 15)} · `
      : ''
  }${channelMembers.length} Members`;

  const isChannel = !type?.includes('TH');
  const isCustomerThread = type === ChannelTypeEnum.CUSTOMERTHREAD;
  const isApp = type === ChannelTypeEnum.APP;
  const isIssue = type === ChannelTypeEnum.ISSUETHREAD;
  const isMyIssue = isIssue && uuid === ownerId;
  const isClosedThread = isIssue && issueStatus === IssueStatus.CLOSED;
  const isGroupThread = !isCustomerThread && !isIssue;

  const hideInput = useMemo(
    () =>
      isEmpty(channelId) ||
      isClosedThread ||
      ([ChannelTypeEnum.PRINCIPAL, ChannelTypeEnum.APP] as string[]).includes(
        type || ''
      ),
    [channelId, isClosedThread, type]
  );

  const hideKeyboardIcon = useMemo(() => {
    return growQueryConversationThread?.isGrowConversation;
  }, [growQueryConversationThread]);

  return {
    channelId,
    type,
    channelName,
    customerId,
    channelMembersNames,
    channelMembersCount,
    isApp,
    isMyIssue,
    isActive,
    isClosedThread,
    hideInput,
    isChannel,
    emojiId,
    sellerWorkspaceId:
      sellerWorkspaceId ||
      selectedPrincipal?.principalWorkspaceId ||
      workspaceId,
    locked: isLockedCustomer,
    isCustomerThread,
    emptyChannelMessage: isChannel
      ? i18nService.t('chats:EMPTY_CHANNEL_MESSAGE')
      : isCustomerThread
        ? i18nService.t('chats:EMPTY_CUSTOMER_THREAD_MESSAGE')
        : i18nService
            .t('chats:EMPTY_THREAD_MESSAGE')
            .replace('X', channelMembersCount.toString()),
    issueStatus,
    transactionType,
    optedOut,
    marketingMessageLimitExhausted,
    customerDetails: customerInfo,
    customerName,
    customerCode,
    channelType,
    channelSubType,
    isGroupThread,
    hideKeyboardIcon,
    unReadMessagesCount: unreadCount,
  };
};
